import _extends from "@babel/runtime/helpers/extends";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _createSuper from "@babel/runtime/helpers/createSuper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _each from "lodash/each";
import _isEmpty from "lodash/isEmpty";
import _get from "lodash/get";
import _reduce from "lodash/reduce";
import _mapValues from "lodash/mapValues";
import _isFunction from "lodash/isFunction";
import React, { useContext } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import hash from './lib/hash';
import getDisplayName from './lib/get-display-name';
import DataProvidersContext from './data-providers-context';
import { propType as dataProvidersPropType, dataDependencyPropType } from './context/data-providers'; // re-export the data-dependencies prop-type for implementing components to re-use

export { dataDependencyPropType };
export default function withDataDependencies(Component, dataDependencies) {
  var displayName = getDisplayName(Component);

  if (!_isFunction(dataDependencies)) {
    throw new Error("withDataDependencies(".concat(displayName, ", dataDependencies) HOC requires dataDependencies function supplied as the second argument, recieved ").concat(dataDependencies));
  }

  function mapAliasesToProps(aliases, dataProviders) {
    return _mapValues(aliases, function (id) {
      return dataProviders.store.get(id);
    });
  }

  function loadDataDependencies(_ref, dataProviders) {
    var aliases = _ref.aliases,
        dependencies = _ref.dependencies;

    var descriptions = _reduce(dependencies, function (memo, description, prop) {
      var id = _get(aliases, prop);

      var data = dataProviders.store.get(id); // no need to refetch if data is already there

      if (data && _get(data, 'value')) return memo; // prevent endless re-fetches on error

      if (data && _get(data, 'error')) return memo; // fetch this dataDescription

      memo.push(description);
      return memo;
    }, []);

    if (_isEmpty(descriptions)) return; // initiate loading of needed data dependencies

    dataProviders.load(descriptions);
  }

  var WithDataProviders = /*#__PURE__*/function (_React$Component) {
    _inherits(WithDataProviders, _React$Component);

    var _super = _createSuper(WithDataProviders);

    function WithDataProviders(props) {
      var _this;

      _classCallCheck(this, WithDataProviders);

      _this = _super.call(this, props);

      _defineProperty(_assertThisInitialized(_this), "onUpdate", function (data) {
        var _this$state = _this.state,
            aliases = _this$state.aliases,
            store = _this$state.store;
        var dataProviders = _this.props.dataProviders;

        _each(aliases, function (id, propName) {
          if (store[propName] === data[id]) return undefined; // continue the loop
          // data for this component changed in the store
          // update the state with the new data for data-dependent props

          _this.setState({
            store: mapAliasesToProps(aliases, dataProviders)
          });

          return false; // break the loop
        });
      });

      _this.state = {};
      return _this;
    }

    _createClass(WithDataProviders, [{
      key: "componentDidMount",
      value: function componentDidMount() {
        var dataProviders = this.props.dataProviders;
        this.unsubscribe = dataProviders.store.subscribe(this.onUpdate);
      }
    }, {
      key: "componentDidUpdate",
      value: function componentDidUpdate(prevProps) {
        var dataProviders = this.props.dataProviders;
        if (dataProviders === prevProps.dataProviders) return;
        this.unsubscribe();
        this.unsubscribe = dataProviders.store.subscribe(this.onUpdate);
      }
    }, {
      key: "componentWillUnmount",
      value: function componentWillUnmount() {
        if (this.unsubscribe) this.unsubscribe();
      }
    }, {
      key: "render",
      value: function render() {
        var componentProps = this.props.componentProps;
        var store = this.state.store;
        return /*#__PURE__*/React.createElement(Component, _extends({}, componentProps, store));
      }
    }], [{
      key: "getDerivedStateFromProps",
      value: function getDerivedStateFromProps(props) {
        var componentProps = props.componentProps,
            dataProviders = props.dataProviders; // process dataDependencies with current props

        var dependencies = dataDependencies(componentProps);

        var aliases = _mapValues(dependencies, function (description) {
          return hash(description);
        }); // initialize loading of data-descriptions here
        // on creation, this happens right after `constructor` is called
        // which kicks off the data requests as soon as possible (required for SSR scenarios)


        loadDataDependencies({
          aliases: aliases,
          dependencies: dependencies
        }, dataProviders); // now there is state for the data-descriptions to build a local store

        var store = mapAliasesToProps(aliases, dataProviders);
        return {
          aliases: aliases,
          store: store
        };
      }
    }]);

    return WithDataProviders;
  }(React.Component);

  if (process.env.NODE_ENV !== 'production') {
    var PropTypes = require('prop-types'); // eslint-disable-line global-require
    // TODO: convert this repo to typescript to remove prop-types usage
    // eslint-disable-next-line react/static-property-placement


    WithDataProviders.propTypes = {
      dataProviders: dataProvidersPropType.isRequired,
      // since this WithDataProviders component is what controls
      // the initialization and gathering of those specific prop
      // aliases, any `isRequired` prop-type for a prop supplied
      // by WithDataProviders would fail validation
      // - therefore -
      // componentProps can be any object because
      // component may define required prop-types for data-dependencies.
      // eslint-disable-next-line react/forbid-prop-types
      componentProps: PropTypes.object.isRequired
    };
  }

  var WithDataDependencies = function WithDataDependencies(props) {
    var dataProviders = useContext(DataProvidersContext);

    if (!dataProviders) {
      throw new Error("withDataDependencies(".concat(displayName, ", dataDependencies) must be rendered inside 'DataProvidersContext' providing an instance of 'dataProviders'"));
    } // TODO: can we refactor WithDataProviders to functional-with-hooks directly?


    return /*#__PURE__*/React.createElement(WithDataProviders, {
      dataProviders: dataProviders,
      componentProps: props
    });
  };

  WithDataDependencies.defaultProps = Component.defaultProps;
  WithDataDependencies.displayName = "WithDataDependencies(".concat(displayName, ")");
  hoistNonReactStatics(WithDataDependencies, Component);
  return WithDataDependencies;
}